import { z } from 'zod';
import { IdSchema, IsoDateTimeSchema } from './common.js';
import { HexColorSchema, TrainingDesignSchema, TrainingFontSchema, TrainingInstructionsSchema, } from './training.js';
/**
 * A training session's slide deck. Every slide is a fixed SLIDE_WIDTH x SLIDE_HEIGHT canvas of
 * absolutely positioned elements, in canvas pixels. Gemini never places elements itself: it
 * picks a layout and writes the content (GeneratedSlideSchema), and layoutSlide (slides/layout.ts)
 * turns that into elements. From then on the elements are the deck — the editor moves, resizes
 * and restyles them, and renderSlideHtml (slides/render.ts) and the PPTX export both read them.
 */
export const SLIDE_WIDTH = 1920;
export const SLIDE_HEIGHT = 1080;
/** Inline formatting. Anything unset falls back to the paragraph's element role and the deck's
 * design (heading font for titles, body font otherwise; design text color). */
export const TextRunSchema = z.object({
    text: z.string().max(5000),
    bold: z.boolean().optional(),
    italic: z.boolean().optional(),
    underline: z.boolean().optional(),
    color: HexColorSchema.optional(),
    /** Canvas pixels. */
    fontSize: z.number().min(8).max(400).optional(),
    fontFamily: TrainingFontSchema.optional(),
});
export const TEXT_ALIGNS = ['left', 'center', 'right', 'justify'];
export const TextAlignSchema = z.enum(TEXT_ALIGNS);
export const ParagraphSchema = z.object({
    runs: z.array(TextRunSchema).max(200),
    align: TextAlignSchema.optional(),
    bullet: z.boolean().optional(),
});
const BoxShape = {
    id: IdSchema,
    x: z.number(),
    y: z.number(),
    w: z.number().positive(),
    h: z.number().positive(),
    /** Clockwise degrees about the element's centre, 0 (or unset) to under 360. Decks saved before
     * rotation existed have none, which means upright. */
    rotation: z.number().min(0).lt(360).optional(),
};
/** An angle in degrees brought into [0, 360), rounded to a tenth of a degree. */
export function normalizeRotation(degrees) {
    const r = Math.round((((degrees % 360) + 360) % 360) * 10) / 10;
    return r >= 360 ? 0 : r;
}
/** `role` picks the default font and size: titles use the design's heading font. */
export const TEXT_ROLES = ['title', 'subtitle', 'body', 'caption'];
export const TextRoleSchema = z.enum(TEXT_ROLES);
export const TextElementSchema = z.object({
    ...BoxShape,
    type: z.literal('text'),
    role: TextRoleSchema.default('body'),
    paragraphs: z.array(ParagraphSchema).max(100),
    verticalAlign: z.enum(['top', 'middle', 'bottom']).default('top'),
});
/** An uploaded image, or — until the user uploads one — a placeholder showing what the outline
 * asked for. */
export const ImageElementSchema = z.object({
    ...BoxShape,
    type: z.literal('image'),
    storagePath: z.string().max(1024).nullable().default(null),
    placeholder: z.string().max(1000).optional(),
    fit: z.enum(['cover', 'contain']).default('contain'),
});
/** A diagram Gemini drew as SVG. Always rendered through an <img> data URI, never inlined into
 * the page, so any script or external reference in the markup can't run or load. */
export const DIAGRAM_SVG_MAX_LENGTH = 60_000;
export const DiagramElementSchema = z.object({
    ...BoxShape,
    type: z.literal('diagram'),
    svg: z.string().max(DIAGRAM_SVG_MAX_LENGTH),
    description: z.string().max(1000).default(''),
});
export const SHAPE_KINDS = ['rect', 'roundRect', 'ellipse', 'line'];
export const ShapeElementSchema = z.object({
    ...BoxShape,
    type: z.literal('shape'),
    shape: z.enum(SHAPE_KINDS),
    fill: HexColorSchema.nullable().default(null),
    stroke: HexColorSchema.nullable().default(null),
    strokeWidth: z.number().min(0).max(100).default(0),
});
export const SlideElementSchema = z.discriminatedUnion('type', [
    TextElementSchema,
    ImageElementSchema,
    DiagramElementSchema,
    ShapeElementSchema,
]);
/** Elements are painted in array order — later ones on top. */
export const SlideSchema = z.object({
    id: IdSchema,
    background: HexColorSchema,
    elements: z.array(SlideElementSchema).max(100),
    speakerNotes: z.string().max(10_000).default(''),
    /** Show the slide's number in its corner. The number itself isn't stored — it's the slide's
     * position, worked out when rendering, so it stays right when slides are reordered. */
    showNumber: z.boolean().optional(),
});
export const DeckSchema = z.object({
    /** The design the deck was generated with — fonts and colors elements fall back to. */
    design: TrainingDesignSchema,
    slides: z.array(SlideSchema).min(1).max(100),
});
export const DECK_VERSION_SOURCES = ['llm', 'manual', 'restore'];
export const DeckVersionSourceSchema = z.enum(DECK_VERSION_SOURCES);
/** projects/{id}/trainingSessions/{id}/deckVersions/{id} — immutable once written. */
export const DeckVersionSchema = z.object({
    id: IdSchema,
    versionNumber: z.number().int().positive(),
    parentVersionId: IdSchema.nullable(),
    deck: DeckSchema,
    source: DeckVersionSourceSchema,
    authorUid: IdSchema,
    /** source 'llm' only: the instructions that run used, after overrides. */
    instructionsUsed: TrainingInstructionsSchema.optional(),
    /** source 'restore' only. */
    restoredFromVersionId: IdSchema.optional(),
    changeSummary: z.string().max(500).optional(),
    llmRunId: IdSchema.nullable().default(null),
    createdAt: IsoDateTimeSchema,
});
/** projects/{id}/trainingSessions/{id}/deckDraft/current — the editor's autosave between
 * explicit saves. Saving a version deletes it. */
export const DeckDraftSchema = z.object({
    deck: DeckSchema,
    baseVersionId: IdSchema.nullable(),
    updatedAt: IsoDateTimeSchema,
    updatedByUid: IdSchema,
});
export const SaveDeckDraftInputSchema = z.object({
    deck: DeckSchema,
    baseVersionId: IdSchema.nullable(),
});
export const SaveDeckVersionInputSchema = z.object({
    deck: DeckSchema,
    baseVersionId: IdSchema.nullable(),
    changeSummary: z.string().max(500).optional(),
});
// ---- What Gemini generates ----
export const SLIDE_LAYOUTS = [
    /** Opening slide: big title, subtitle. */
    'title',
    /** Divider between parts of the session. */
    'section',
    'bullets',
    'two_columns',
    /** Text on the left, the visual on the right. */
    'visual_right',
    'visual_left',
    /** The visual fills most of the slide, with a title above. */
    'visual_full',
    'quote',
    /** One key sentence as the whole slide, at display size. */
    'statement',
    /** 2-6 cards in a grid, each a heading and a short text (items). */
    'cards',
    /** A numbered sequence of 3-5 steps, left to right (items). */
    'steps',
    /** 1-3 key figures at display size, each with a label (stats). */
    'stats',
    /** Closing slide: recap or questions. */
    'closing',
];
export const SlideLayoutSchema = z.enum(SLIDE_LAYOUTS);
/** One slide as Gemini writes it. Bullets may use **bold** and *italic* markers. */
export const GeneratedSlideSchema = z.object({
    layout: SlideLayoutSchema,
    title: z.string().min(1).max(200),
    subtitle: z.string().max(300).optional(),
    bullets: z.array(z.string().min(1).max(400)).max(8).optional(),
    columns: z
        .array(z.object({
        heading: z.string().min(1).max(120),
        bullets: z.array(z.string().min(1).max(300)).max(6),
    }))
        .max(2)
        .optional(),
    quote: z
        .object({ text: z.string().min(1).max(600), attribution: z.string().max(200).optional() })
        .optional(),
    /** cards and steps only. */
    items: z
        .array(z.object({ heading: z.string().min(1).max(80), text: z.string().max(240) }))
        .max(6)
        .optional(),
    /** stats only. value is the figure itself ("48h", "92%", "3x"). */
    stats: z
        .array(z.object({ value: z.string().min(1).max(16), label: z.string().min(1).max(160) }))
        .max(3)
        .optional(),
    visual: z
        .object({
        kind: z.enum(['illustration', 'image', 'diagram']),
        description: z.string().min(1).max(1000),
        /** kind 'diagram' only: a complete standalone <svg> with a viewBox. */
        svg: z.string().max(DIAGRAM_SVG_MAX_LENGTH).optional(),
    })
        .optional(),
    speakerNotes: z.string().min(1).max(5000),
});
export const GeneratedDeckSchema = z.object({
    slides: z.array(GeneratedSlideSchema).min(1).max(100),
});
//# sourceMappingURL=slides.js.map